import React, { useEffect } from 'react';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';
import { baseURL2022Global } from '@ozoneuniverse/mfe-core/dist/components/gltfs/urlsBase';
import { getSkyboxList } from '@ozoneuniverse/mfe-core/dist/components/skyBox-manager';
import { EnvMapType } from '@src/components/generated/graphql';

const hdrSkyboxTexture = {
  day: `${baseURL2022Global}Skybox/venice_sunset_1k.hdr`,
  night: `${baseURL2022Global}Skybox/qatarNight.hdr`,
  arena: `${baseURL2022Global}Skybox/10/lonely_road_afternoon_puresky_4k.hdr`,
};

const cleanMaterials = ({ scene, disposedResource }) => {
  if (!scene) return;

  try {
    scene.traverse((object) => {
      if (object.isMesh && object.material) {
        const materials = Array.isArray(object.material)
          ? object.material
          : [object.material];
        materials.forEach((material) => {
          if (material.envMap === disposedResource) {
            material.envMap = null;
            material.needsUpdate = true;
          }
        });
      }
    });
  } catch (error) {
    console.warn('Error clearing material environment maps:', error);
  }
};

const loadHDRSkybox = ({
  scene,
  renderer,
  skyBoxName,
  environmentIntensity = 0,
  hdriEnvOnly = true,
  envMapType = EnvMapType.Hdri,
}) =>
  new Promise((resolve, reject) => {
    const url = hdrSkyboxTexture[skyBoxName];

    if (typeof url != 'string') {
      reject('url is not a string.');
      return;
    }

    new RGBELoader().load(hdrSkyboxTexture[skyBoxName], (texture) => {
      texture.mapping = THREE.EquirectangularReflectionMapping;

      const pmremGenerator = new THREE.PMREMGenerator(renderer);
      const envMap = pmremGenerator.fromEquirectangular(texture).texture;

      if (envMapType === EnvMapType.Hdri) {
        if (scene.environment) {
          cleanMaterials({ scene, disposedResource: scene.environment });
          scene.environment.dispose();
        }
        scene.environment = envMap;
        scene.environmentIntensity = environmentIntensity;
      }

      if (!hdriEnvOnly) {
        if (scene.background) {
          scene.background.dispose();
        }
        scene.background = envMap;
      }

      pmremGenerator.dispose();

      resolve(true);
    });
  });

const loadCubemap = ({
  item,
  models = [],
  environmentIntensity = 0,
  envMapType = EnvMapType.Hdri,
  scene,
  renderer: gl,
}) => {
  return new Promise((resolve, reject) => {
    if (!item.fileName || !item.url) {
      console.warn('Missing fileName or url in item:', item);
      resolve(false);
      return;
    }

    const loader = new THREE.CubeTextureLoader();
    const texture = loader.load(
      item.fileName.map((name) => {
        return item.url + name;
      })
    );
    (texture as any).colorSpace = (THREE as any).SRGBColorSpace;

    // Set the scene background property to the resulting texture.
    if (envMapType === EnvMapType.SkyBox) {
      if (scene.environment) {
        cleanMaterials({ scene, disposedResource: scene.environment });
        scene.environment.dispose();
      }
      scene.environment = texture;
      scene.environmentIntensity = environmentIntensity;
    }

    if (scene.background) {
      scene.background.dispose();
    }
    scene.background = texture;

    resolve(true);
  });
};

export function Skybox({ item }) {
  const { scene, camera, gl }: any = useThree();

  useEffect(() => {
    if (!item || !item.hdriType) {
      return;
    }
    let skyBox = item;

    if (item.isSkyBox2025) {
      const skyBoxes = [...getSkyboxList('2025')];

      skyBox = {
        ...item,
        ...skyBoxes[item.index + 1],
        url: skyBoxes[item.index + 1].baseUrl,
      };
    }

    loadCubemap({
      item: skyBox,
      scene,
      renderer: gl,
      models: scene.children,
      environmentIntensity: item.hdriIntensity,
      envMapType: item.envMapType,
    }).catch((error) => {
      console.error('Skybox: Failed to load cubemap', error);
    });

    loadHDRSkybox({
      scene,
      renderer: gl,
      skyBoxName: item.hdriType,
      environmentIntensity: item.hdriIntensity,
      envMapType: item.envMapType,
      hdriEnvOnly: true,
    }).catch((error) => {
      console.error('Skybox: Failed to load HDR skybox', error);
    });

    if (item.envMapType === EnvMapType.None) {
      scene.environment = null;
      scene.environmentIntensity = 0;
    }

    scene.children.forEach(async (model: any) => {
      if (model.isMesh) {
        await gl.compileAsync(model, camera, scene);
      }
    });
  }, [item]);
  // The CubeTextureLoader load method takes an array of urls representing all 6 sides of the cube.
  return null;
}
